import { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "@/App";
import { toast } from "sonner";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { CreditCard, Truck, CheckCircle, AlertCircle, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const CheckoutPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const [cart, setCart] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [yocoPublicKey, setYocoPublicKey] = useState(null);
  const [shippingAddress, setShippingAddress] = useState({
    name: user?.name || "",
    email: user?.email || "",
    phone: "",
    address: "",
    city: "",
    postalCode: "",
    country: "South Africa",
  });
  const [shippingRates, setShippingRates] = useState({});

  // Check for Yoco callback params
  useEffect(() => {
    const success = searchParams.get("success");
    const cancelled = searchParams.get("cancelled");
    const failed = searchParams.get("failed");
    const checkoutId = searchParams.get("id");
    
    if (success === "true" && checkoutId) {
      // Payment was successful via redirect
      handlePaymentSuccess(checkoutId);
    } else if (cancelled === "true") {
      toast.error("Payment was cancelled");
    } else if (failed === "true") {
      toast.error("Payment failed. Please try again.");
    }
  }, [searchParams]);

  const handlePaymentSuccess = async (checkoutId) => {
    setProcessing(true);
    try {
      // Verify payment
      const verifyRes = await axios.post(`${API}/payment/verify`, { checkout_id: checkoutId });
      
      if (verifyRes.data.verified) {
        // Get shipping address from localStorage (saved before redirect)
        const savedAddress = localStorage.getItem("fanworld_shipping");
        const address = savedAddress ? JSON.parse(savedAddress) : shippingAddress;
        
        // Create order
        await axios.post(`${API}/orders`, {
          shipping_address: address,
          payment_id: verifyRes.data.payment_id || checkoutId,
        });
        
        localStorage.removeItem("fanworld_shipping");
        setPaymentSuccess(true);
        toast.success("Order placed successfully! Check your email for confirmation.");
        
        setTimeout(() => {
          navigate("/orders");
        }, 3000);
      } else {
        toast.error("Payment verification failed");
      }
    } catch (error) {
      toast.error("Failed to complete order");
    } finally {
      setProcessing(false);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch cart
        const cartRes = await axios.get(`${API}/cart`);
        if (!cartRes.data.items || cartRes.data.items.length === 0) {
          toast.error("Your cart is empty");
          navigate("/cart");
          return;
        }
        setCart(cartRes.data);
        
        // Fetch Yoco public key
        try {
          const configRes = await axios.get(`${API}/payment/config`);
          setYocoPublicKey(configRes.data.public_key);
        } catch (e) {
          console.error("Failed to get payment config");
        }
        try {
          const ratesRes = await axios.get(`${API}/shipping/rates`);
          setShippingRates(ratesRes.data);
        } catch (e) {
          console.error("Failed to get shipping rates");
        }
      } catch (error) {
        toast.error("Failed to load cart");
        navigate("/cart");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [navigate]);

  // Load Yoco SDK
  useEffect(() => {
    if (yocoPublicKey && !window.YocoSDK) {
      const script = document.createElement("script");
      script.src = "https://js.yoco.com/sdk/v1/yoco-sdk-web.js";
      script.async = true;
      document.body.appendChild(script);
    }
  }, [yocoPublicKey]);

  const handleInputChange = (e) => {
    setShippingAddress({
      ...shippingAddress,
      [e.target.name]: e.target.value,
    });
  };

  const validateForm = () => {
    const required = ["name", "email", "phone", "address", "city", "postalCode"];
    for (const field of required) {
      if (!shippingAddress[field]) {
        toast.error(`Please fill in ${field.replace(/([A-Z])/g, " $1").toLowerCase()}`);
        return false;
      }
    }
    return true;
  };

  const handlePayment = async () => {
    if (!validateForm()) return;
    
    setProcessing(true);
    
    try {
      // Save shipping address for after redirect
      localStorage.setItem("fanworld_shipping", JSON.stringify(shippingAddress));
      
      // Try Yoco Checkout API first
      try {
        const checkoutRes = await axios.post(`${API}/payment/create-checkout`);
        
        if (checkoutRes.data.redirect_url) {
          // Redirect to Yoco hosted checkout
          window.location.href = checkoutRes.data.redirect_url;
          return;
        }
      } catch (checkoutError) {
        console.log("Yoco redirect checkout not available, using inline popup");
      }
      
      // Fallback to Yoco inline popup
      if (window.YocoSDK && yocoPublicKey) {
        const yoco = new window.YocoSDK({
          publicKey: yocoPublicKey,
        });
        
        yoco.showPopup({
          amountInCents: Math.round((cart.total + getShippingCost()) * 100),
          currency: "ZAR",
          name: "FanVerse Store",
          description: `Order - ${cart.items.length} items`,
          callback: async (result) => {
            if (result.error) {
              toast.error(result.error.message || "Payment failed");
              setProcessing(false);
            } else {
              // Payment successful
              try {
                await axios.post(`${API}/orders`, {
                  shipping_address: shippingAddress,
                  payment_id: result.id,
                });
                
                localStorage.removeItem("fanworld_shipping");
                setPaymentSuccess(true);
                toast.success("Order placed successfully! Check your email for confirmation.");
                
                setTimeout(() => {
                  navigate("/orders");
                }, 3000);
              } catch (orderError) {
                toast.error("Payment succeeded but order creation failed. Please contact support.");
              }
              setProcessing(false);
            }
          }
        });
      } else {
        // Final fallback - direct order creation (for testing)
        const paymentId = `manual_${Date.now()}`;
        
        await axios.post(`${API}/orders`, {
          shipping_address: shippingAddress,
          payment_id: paymentId,
        });
        
        localStorage.removeItem("fanworld_shipping");
        setPaymentSuccess(true);
        toast.success("Order placed successfully! Check your email for confirmation.");
        
        setTimeout(() => {
          navigate("/orders");
        }, 3000);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Payment failed. Please try again.");
      setProcessing(false);
    }
  };

  const getShippingCost = () => {
    const country = shippingAddress.country;
    const rates = shippingRates[country] || shippingRates["Other"] || { standard: 250, free_threshold: 1500 };
    if (cart.total >= rates.free_threshold) return 0;
    return rates.standard;
  };

  const shippingCost = getShippingCost();
  const totalWithShipping = cart.total + shippingCost;
  const currentRates = shippingRates[shippingAddress.country] || shippingRates["Other"] || { standard: 250, free_threshold: 1500 };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#030712]">
        <Navbar />
        <div className="spinner"></div>
      </div>
    );
  }

  if (paymentSuccess) {
    return (
      <div className="min-h-screen flex flex-col bg-[#030712]">
        <Navbar />
        <main className="flex-1 flex items-center justify-center">
          <div className="text-center max-w-md mx-auto px-4">
            <CheckCircle className="w-20 h-20 text-emerald-500 mx-auto mb-6" />
            <h1 className="text-3xl font-black text-white mb-4 font-['Outfit']">Order Confirmed!</h1>
            <p className="text-slate-400 mb-2">Thank you for your purchase.</p>
            <div className="flex items-center justify-center gap-2 text-amber-500 mb-6">
              <Mail className="w-5 h-5" />
              <span className="text-sm">Confirmation email sent to {shippingAddress.email || user?.email}</span>
            </div>
            <p className="text-slate-500 text-sm mb-4">Redirecting to orders...</p>
            <div className="spinner mx-auto"></div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-[#030712]">
      <Navbar />
      
      <main className="flex-1 pt-24 pb-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-4 mb-8">
            <CreditCard className="w-8 h-8 text-yellow-500" />
            <h1 className="text-3xl font-black text-white font-['Outfit']">Checkout</h1>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Shipping Form */}
            <div className="lg:col-span-2">
              <div className="card-surface p-6 mb-6">
                <div className="flex items-center gap-3 mb-6">
                  <Truck className="w-5 h-5 text-amber-500" />
                  <h2 className="text-lg font-bold text-white font-['Outfit']">Shipping Information</h2>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-slate-300">Full Name</Label>
                    <Input
                      name="name"
                      value={shippingAddress.name}
                      onChange={handleInputChange}
                      className="input-dark mt-1"
                      required
                      data-testid="shipping-name"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-300">Email</Label>
                    <Input
                      name="email"
                      type="email"
                      value={shippingAddress.email}
                      onChange={handleInputChange}
                      className="input-dark mt-1"
                      required
                      data-testid="shipping-email"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-300">Phone Number</Label>
                    <Input
                      name="phone"
                      value={shippingAddress.phone}
                      onChange={handleInputChange}
                      className="input-dark mt-1"
                      required
                      data-testid="shipping-phone"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-300">Country</Label>
                    <select
                      name="country"
                      value={shippingAddress.country}
                      onChange={handleInputChange}
                      className="w-full mt-1 rounded-md bg-[#0B0F19] border border-white/10 text-white px-3 py-2 text-sm focus:border-amber-500 focus:outline-none"
                      data-testid="shipping-country"
                    >
                      {Object.keys(shippingRates).length > 0 ? (
                        Object.keys(shippingRates).map(c => (
                          <option key={c} value={c}>{c}</option>
                        ))
                      ) : (
                        <option value="South Africa">South Africa</option>
                      )}
                    </select>
                  </div>
                  <div className="md:col-span-2">
                    <Label className="text-slate-300">Street Address</Label>
                    <Input
                      name="address"
                      value={shippingAddress.address}
                      onChange={handleInputChange}
                      className="input-dark mt-1"
                      required
                      data-testid="shipping-address"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-300">City</Label>
                    <Input
                      name="city"
                      value={shippingAddress.city}
                      onChange={handleInputChange}
                      className="input-dark mt-1"
                      required
                      data-testid="shipping-city"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-300">Postal Code</Label>
                    <Input
                      name="postalCode"
                      value={shippingAddress.postalCode}
                      onChange={handleInputChange}
                      className="input-dark mt-1"
                      required
                      data-testid="shipping-postal"
                    />
                  </div>
                </div>
              </div>

              {/* Payment Info */}
              <div className="card-surface p-6">
                <div className="flex items-center gap-3 mb-4">
                  <CreditCard className="w-5 h-5 text-amber-500" />
                  <h2 className="text-lg font-bold text-white font-['Outfit']">Payment</h2>
                </div>
                <div className="flex items-center gap-3 p-4 bg-emerald-500/10 border border-emerald-500/20 mb-4">
                  <CheckCircle className="w-5 h-5 text-emerald-500 flex-shrink-0" />
                  <p className="text-sm text-emerald-200">
                    Secure payment powered by Yoco. Your card details are encrypted.
                  </p>
                </div>
                <div className="flex items-center gap-3 p-4 bg-amber-500/10 border border-amber-500/20">
                  <Mail className="w-5 h-5 text-amber-500 flex-shrink-0" />
                  <p className="text-sm text-amber-200">
                    Order confirmation will be sent to {shippingAddress.email || user?.email}
                  </p>
                </div>
              </div>
            </div>

            {/* Order Summary */}
            <div className="lg:col-span-1">
              <div className="card-surface p-6 sticky top-24">
                <h2 className="text-lg font-bold text-white mb-4 font-['Outfit']">Order Summary</h2>
                
                <div className="space-y-3 mb-4 max-h-48 overflow-y-auto">
                  {cart.items.map((item) => (
                    <div key={item.id} className="flex justify-between text-sm">
                      <span className="text-slate-400">
                        {item.product_name} x{item.quantity}
                        {item.size && ` (${item.size})`}
                      </span>
                      <span className="text-white">R{(item.price * item.quantity).toFixed(2)}</span>
                    </div>
                  ))}
                </div>

                <div className="space-y-3 text-sm pt-4 border-t border-white/10">
                  <div className="flex justify-between text-slate-400">
                    <span>Subtotal</span>
                    <span>R{cart.total.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-slate-400">
                    <span>Shipping ({shippingAddress.country})</span>
                    <span>{shippingCost === 0 ? "Free" : `R${shippingCost.toFixed(2)}`}</span>
                  </div>
                  {shippingCost > 0 && (
                    <p className="text-xs text-slate-500">Free shipping over R{currentRates.free_threshold}</p>
                  )}
                  <div className="pt-3 border-t border-white/10 flex justify-between text-white font-bold text-lg">
                    <span>Total</span>
                    <span>R{totalWithShipping.toFixed(2)}</span>
                  </div>
                </div>

                <Button
                  onClick={handlePayment}
                  disabled={processing}
                  className="btn-primary w-full mt-6 py-3"
                  data-testid="pay-btn"
                >
                  {processing ? "Processing..." : `Pay R${totalWithShipping.toFixed(2)}`}
                </Button>

                <p className="text-xs text-slate-500 text-center mt-4">
                  By placing this order, you agree to our terms and conditions.
                </p>
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default CheckoutPage;
